import { Injectable } from '@angular/core';

/**
 * Anything the coordinator can close. Kept as a minimal interface (rather than
 * importing DropdownMenuComponent) so the service and the component don't form
 * a circular import.
 */
export interface ClosableDropdown {
  closeDropdown(): void;
}

/**
 * Single source of truth for "only one dropdown open at a time".
 *
 * Every DropdownMenuComponent registers itself on init and unregisters on
 * destroy. When one opens it calls `closeOthers( this )`, which shuts every
 * other registered menu. This replaces the old per-nav `closeOtherDropDowns`
 * bookkeeping so Services / Plugins / Tools / Settings all coordinate the same
 * way, in both the top nav and the bottom nav.
 */
@Injectable( { providedIn: 'root' } )
export class DropdownCoordinatorService {
  private menus = new Set<ClosableDropdown>();

  register( menu: ClosableDropdown ) {
    this.menus.add( menu );
  }

  unregister( menu: ClosableDropdown ) {
    this.menus.delete( menu );
  }

  /** Close every registered dropdown except `keepOpen` (pass nothing to close all). */
  closeOthers( keepOpen?: ClosableDropdown ) {
    this.menus.forEach( menu => {
      if ( menu !== keepOpen ) {
        menu.closeDropdown();
      }
    } );
  }
}
